import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalActions } from "@/components/Modal";
import { useAuth } from "@/hooks/useAuth";
import {
  getSeenAnnouncementIds,
  listActiveAnnouncements,
  listUnseenUserNotifications,
  markAnnouncementSeen,
  markUserNotificationSeen,
  type UserNotification,
} from "./operations";
import type { AnnouncementWithId } from "@amazing-wedding/shared-types";

type PendingBroadcast = { source: "announcement"; doc: AnnouncementWithId };
type PendingPersonal = { source: "notification"; doc: UserNotification };
type Pending = PendingBroadcast | PendingPersonal;

/**
 * Mounted once globally for signed-in users. On every sign-in / app load it
 * looks for two kinds of one-time modals the user hasn't seen yet:
 *
 *  - Broadcast announcements at /announcements (admin-authored, shown to all).
 *    Dismissals stored at /users/{uid}/seenAnnouncements/{id}.
 *  - Personal notifications at /users/{uid}/notifications (admin-authored,
 *    e.g. plan upgrade). Dismissals stamp `seenAt` on the doc itself.
 *
 * Personal notifications take priority — they are usually account-state
 * changes the user needs to see before generic announcements.
 */
export function AnnouncementWatcher() {
  const { firebaseUser, isSignedIn } = useAuth();
  const uid = firebaseUser?.uid ?? null;
  const [pending, setPending] = useState<Pending | null>(null);

  useEffect(() => {
    if (!isSignedIn || !uid) {
      setPending(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const [active, personal] = await Promise.all([
          listActiveAnnouncements(),
          listUnseenUserNotifications(uid),
        ]);
        if (cancelled) return;

        // Personal notifications take priority — they are usually account-state
        // changes (e.g. plan upgrade) the user needs to see before generic
        // broadcasts.
        if (personal.length > 0) {
          const firstNotification = personal[0];
          if (firstNotification) {
            setPending({ source: "notification", doc: firstNotification });
            return;
          }
        }
        const seen = await getSeenAnnouncementIds(
          uid,
          active.map((a) => a.id),
        );
        if (cancelled) return;
        const nextAnnouncement = active.find((a) => !seen.has(a.id));
        if (nextAnnouncement) {
          setPending({ source: "announcement", doc: nextAnnouncement });
          return;
        }
        setPending(null);
      } catch (err) {
        console.warn("[AnnouncementWatcher] Failed to load announcements:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, uid]);

  if (!pending || !uid) return null;

  return <AnnouncementModal uid={uid} pending={pending} onClose={() => setPending(null)} />;
}

function AnnouncementModal({
  uid,
  pending,
  onClose,
}: {
  uid: string;
  pending: Pending;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  async function handleDismiss() {
    onClose();
    try {
      if (pending.source === "announcement") {
        await markAnnouncementSeen(uid, pending.doc.id);
      } else {
        await markUserNotificationSeen(uid, pending.doc.id);
      }
    } catch (err) {
      console.warn("[AnnouncementWatcher] Failed to mark seen:", err);
    }
  }

  return (
    <Modal title={pending.doc.title} onClose={handleDismiss} closeOnBackdrop={false}>
      <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.55, color: "var(--ink-1)" }}>
        {pending.doc.body}
      </p>
      <ModalActions>
        <button type="button" className="btn btn-primary" onClick={handleDismiss}>
          {t("announcements.dismiss")}
        </button>
      </ModalActions>
    </Modal>
  );
}
